import { supabase } from '../lib/supabase';

/**
 * Travel Inventory Service
 * Manages all travel-related data including destinations, hotels, transport, activities, restaurants, and packages
 */

// ==================== DESTINATIONS ====================

/**
 * Fetch all destinations with optional filters
 * @param {Object} filters - Optional filters (country, type, featured, active)
 * @returns {Promise<Array>} List of destinations
 */
export const getDestinations = async (filters = {}) => {
  try {
    let query = supabase?.from('destinations')?.select('*')?.eq('is_active', true)?.order('popularity_score', { ascending: false });

    if (filters?.country) {
      query = query?.eq('country', filters?.country);
    }
    if (filters?.type) {
      query = query?.eq('destination_type', filters?.type);
    }
    if (filters?.featured !== undefined) {
      query = query?.eq('is_featured', filters?.featured);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching destinations:', error);
    throw error;
  }
};

/**
 * Fetch a single destination by ID
 * @param {string} id - Destination ID
 * @returns {Promise<Object>} Destination details
 */
export const getDestinationById = async (id) => {
  try {
    const { data, error } = await supabase?.from('destinations')?.select('*')?.eq('id', id)?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching destination:', error);
    throw error;
  }
};

/**
 * Create a new destination
 * @param {Object} destinationData - Destination information
 * @returns {Promise<Object>} Created destination
 */
export const createDestination = async (destinationData) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    
    const { data, error } = await supabase?.from('destinations')?.insert([{
        ...destinationData,
        created_by: user?.id
      }])?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating destination:', error);
    throw error;
  }
};

/**
 * Update a destination
 * @param {string} id - Destination ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated destination
 */
export const updateDestination = async (id, updates) => {
  try {
    const { data, error } = await supabase?.from('destinations')?.update({
        ...updates,
        updated_at: new Date()?.toISOString()
      })?.eq('id', id)?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating destination:', error);
    throw error;
  }
};

/**
 * Delete a destination
 * @param {string} id - Destination ID
 * @returns {Promise<boolean>} Success status
 */
export const deleteDestination = async (id) => {
  try {
    const { error } = await supabase?.from('destinations')?.delete()?.eq('id', id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting destination:', error);
    throw error;
  }
};

// ==================== HOTELS ====================

/**
 * Fetch hotels with optional filters
 * @param {Object} filters - Optional filters (destinationId, type, priceRange, rating)
 * @returns {Promise<Array>} List of hotels
 */
export const getHotels = async (filters = {}) => {
  try {
    let query = supabase?.from('hotels')?.select('*, destinations(name, country)')?.eq('is_active', true)?.order('average_rating', { ascending: false });

    if (filters?.destinationId) {
      query = query?.eq('destination_id', filters?.destinationId);
    }
    if (filters?.type) {
      query = query?.eq('accommodation_type', filters?.type);
    }
    if (filters?.priceRange) {
      query = query?.eq('price_range', filters?.priceRange);
    }
    if (filters?.minRating) {
      query = query?.gte('average_rating', filters?.minRating);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching hotels:', error);
    throw error;
  }
};

/**
 * Fetch a single hotel by ID with amenities
 * @param {string} id - Hotel ID
 * @returns {Promise<Object>} Hotel details with amenities
 */
export const getHotelById = async (id) => {
  try {
    const { data, error } = await supabase?.from('hotels')?.select('*, destinations(name, country), hotel_amenities(*)')?.eq('id', id)?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching hotel:', error);
    throw error;
  }
};

/**
 * Create a new hotel
 * @param {Object} hotelData - Hotel information
 * @returns {Promise<Object>} Created hotel
 */
export const createHotel = async (hotelData) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    
    const { data, error } = await supabase?.from('hotels')?.insert([{
        ...hotelData,
        created_by: user?.id
      }])?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating hotel:', error);
    throw error;
  }
};

/**
 * Update a hotel
 * @param {string} id - Hotel ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated hotel
 */
export const updateHotel = async (id, updates) => {
  try {
    const { data, error } = await supabase?.from('hotels')?.update({
        ...updates,
        updated_at: new Date()?.toISOString()
      })?.eq('id', id)?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating hotel:', error);
    throw error;
  }
};

/**
 * Delete a hotel
 * @param {string} id - Hotel ID
 * @returns {Promise<boolean>} Success status
 */
export const deleteHotel = async (id) => {
  try {
    const { error } = await supabase?.from('hotels')?.delete()?.eq('id', id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting hotel:', error);
    throw error;
  }
};

/**
 * Add amenity to hotel
 * @param {string} hotelId - Hotel ID
 * @param {Object} amenityData - Amenity information
 * @returns {Promise<Object>} Created amenity
 */
export const addHotelAmenity = async (hotelId, amenityData) => {
  try {
    const { data, error } = await supabase?.from('hotel_amenities')?.insert([{
        hotel_id: hotelId,
        ...amenityData
      }])?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error adding hotel amenity:', error);
    throw error;
  }
};

// ==================== TRANSPORT OPTIONS ====================

/**
 * Fetch transport options with filters
 * @param {Object} filters - Optional filters (originId, destinationId, type)
 * @returns {Promise<Array>} List of transport options
 */
export const getTransportOptions = async (filters = {}) => {
  try {
    let query = supabase?.from('transport_options')?.select('*, origin:destinations!origin_destination_id(name, country), destination:destinations!destination_destination_id(name, country)')?.eq('is_active', true)?.order('price', { ascending: true });

    if (filters?.originId) {
      query = query?.eq('origin_destination_id', filters?.originId);
    }
    if (filters?.destinationId) {
      query = query?.eq('destination_destination_id', filters?.destinationId);
    }
    if (filters?.type) {
      query = query?.eq('transport_type', filters?.type);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching transport options:', error);
    throw error;
  }
};

/**
 * Fetch a single transport option by ID
 * @param {string} id - Transport option ID
 * @returns {Promise<Object>} Transport option details
 */
export const getTransportOptionById = async (id) => {
  try {
    const { data, error } = await supabase?.from('transport_options')?.select('*, origin:destinations!origin_destination_id(name, country), destination:destinations!destination_destination_id(name, country)')?.eq('id', id)?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching transport option:', error);
    throw error;
  }
};

/**
 * Create a new transport option
 * @param {Object} transportData - Transport information
 * @returns {Promise<Object>} Created transport option
 */
export const createTransportOption = async (transportData) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    
    const { data, error } = await supabase?.from('transport_options')?.insert([{
        ...transportData,
        created_by: user?.id
      }])?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating transport option:', error);
    throw error;
  }
};

/**
 * Update a transport option
 * @param {string} id - Transport option ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated transport option
 */
export const updateTransportOption = async (id, updates) => {
  try {
    const { data, error } = await supabase?.from('transport_options')?.update({
        ...updates,
        updated_at: new Date()?.toISOString()
      })?.eq('id', id)?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating transport option:', error);
    throw error;
  }
};

/**
 * Delete a transport option
 * @param {string} id - Transport option ID
 * @returns {Promise<boolean>} Success status
 */
export const deleteTransportOption = async (id) => {
  try {
    const { error } = await supabase?.from('transport_options')?.delete()?.eq('id', id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting transport option:', error);
    throw error;
  }
};

// ==================== ACTIVITIES ====================

/**
 * Fetch activities with filters
 * @param {Object} filters - Optional filters (destinationId, type, priceRange)
 * @returns {Promise<Array>} List of activities
 */
export const getActivities = async (filters = {}) => {
  try {
    let query = supabase?.from('activities')?.select('*, destinations(name, country)')?.eq('is_active', true)?.order('average_rating', { ascending: false });

    if (filters?.destinationId) {
      query = query?.eq('destination_id', filters?.destinationId);
    }
    if (filters?.type) {
      query = query?.eq('activity_type', filters?.type);
    }
    if (filters?.priceRange) {
      query = query?.eq('price_range', filters?.priceRange);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching activities:', error);
    throw error;
  }
};

/**
 * Fetch a single activity by ID
 * @param {string} id - Activity ID
 * @returns {Promise<Object>} Activity details
 */
export const getActivityById = async (id) => {
  try {
    const { data, error } = await supabase?.from('activities')?.select('*, destinations(name, country)')?.eq('id', id)?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching activity:', error);
    throw error;
  }
};

/**
 * Create a new activity
 * @param {Object} activityData - Activity information
 * @returns {Promise<Object>} Created activity
 */
export const createActivity = async (activityData) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    
    const { data, error } = await supabase?.from('activities')?.insert([{
        ...activityData,
        created_by: user?.id
      }])?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating activity:', error);
    throw error;
  }
};

/**
 * Update an activity
 * @param {string} id - Activity ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated activity
 */
export const updateActivity = async (id, updates) => {
  try {
    const { data, error } = await supabase?.from('activities')?.update({
        ...updates,
        updated_at: new Date()?.toISOString()
      })?.eq('id', id)?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating activity:', error);
    throw error;
  }
};

/**
 * Delete an activity
 * @param {string} id - Activity ID
 * @returns {Promise<boolean>} Success status
 */
export const deleteActivity = async (id) => {
  try {
    const { error } = await supabase?.from('activities')?.delete()?.eq('id', id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting activity:', error);
    throw error;
  }
};

// ==================== RESTAURANTS ====================

/**
 * Fetch restaurants with filters
 * @param {Object} filters - Optional filters (destinationId, cuisineType, priceRange)
 * @returns {Promise<Array>} List of restaurants
 */
export const getRestaurants = async (filters = {}) => {
  try {
    let query = supabase?.from('restaurants')?.select('*, destinations(name, country)')?.eq('is_active', true)?.order('average_rating', { ascending: false });

    if (filters?.destinationId) {
      query = query?.eq('destination_id', filters?.destinationId);
    }
    if (filters?.cuisineType) {
      query = query?.eq('cuisine_type', filters?.cuisineType);
    }
    if (filters?.priceRange) {
      query = query?.eq('price_range', filters?.priceRange);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching restaurants:', error);
    throw error;
  }
};

/**
 * Fetch a single restaurant by ID
 * @param {string} id - Restaurant ID
 * @returns {Promise<Object>} Restaurant details
 */
export const getRestaurantById = async (id) => {
  try {
    const { data, error } = await supabase?.from('restaurants')?.select('*, destinations(name, country)')?.eq('id', id)?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching restaurant:', error);
    throw error;
  }
};

/**
 * Create a new restaurant
 * @param {Object} restaurantData - Restaurant information
 * @returns {Promise<Object>} Created restaurant
 */
export const createRestaurant = async (restaurantData) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    
    const { data, error } = await supabase?.from('restaurants')?.insert([{
        ...restaurantData,
        created_by: user?.id
      }])?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating restaurant:', error);
    throw error;
  }
};

/**
 * Update a restaurant
 * @param {string} id - Restaurant ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated restaurant
 */
export const updateRestaurant = async (id, updates) => {
  try {
    const { data, error } = await supabase?.from('restaurants')?.update({
        ...updates,
        updated_at: new Date()?.toISOString()
      })?.eq('id', id)?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating restaurant:', error);
    throw error;
  }
};

/**
 * Delete a restaurant
 * @param {string} id - Restaurant ID
 * @returns {Promise<boolean>} Success status
 */
export const deleteRestaurant = async (id) => {
  try {
    const { error } = await supabase?.from('restaurants')?.delete()?.eq('id', id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting restaurant:', error);
    throw error;
  }
};

// ==================== ATTRACTIONS ====================

/**
 * Fetch attractions with filters
 * @param {Object} filters - Optional filters (destinationId, category, featured)
 * @returns {Promise<Array>} List of attractions
 */
export const getAttractions = async (filters = {}) => {
  try {
    let query = supabase?.from('attractions')?.select('*, destinations(name, country)')?.eq('is_active', true)?.order('average_rating', { ascending: false });

    if (filters?.destinationId) {
      query = query?.eq('destination_id', filters?.destinationId);
    }
    if (filters?.category) {
      query = query?.eq('category', filters?.category);
    }
    if (filters?.featured !== undefined) {
      query = query?.eq('is_featured', filters?.featured);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching attractions:', error);
    throw error;
  }
};

/**
 * Fetch a single attraction by ID
 * @param {string} id - Attraction ID
 * @returns {Promise<Object>} Attraction details
 */
export const getAttractionById = async (id) => {
  try {
    const { data, error } = await supabase?.from('attractions')?.select('*, destinations(name, country)')?.eq('id', id)?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching attraction:', error);
    throw error;
  }
};

/**
 * Create a new attraction
 * @param {Object} attractionData - Attraction information
 * @returns {Promise<Object>} Created attraction
 */
export const createAttraction = async (attractionData) => {
  try {
    const { data, error } = await supabase?.from('attractions')?.insert([attractionData])?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating attraction:', error);
    throw error;
  }
};

/**
 * Update an attraction
 * @param {string} id - Attraction ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated attraction
 */
export const updateAttraction = async (id, updates) => {
  try {
    const { data, error } = await supabase?.from('attractions')?.update({
        ...updates,
        updated_at: new Date()?.toISOString()
      })?.eq('id', id)?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating attraction:', error);
    throw error;
  }
};

/**
 * Delete an attraction
 * @param {string} id - Attraction ID
 * @returns {Promise<boolean>} Success status
 */
export const deleteAttraction = async (id) => {
  try {
    const { error } = await supabase?.from('attractions')?.delete()?.eq('id', id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting attraction:', error);
    throw error;
  }
};

// ==================== TRAVEL PACKAGES ====================

/**
 * Fetch travel packages with filters
 * @param {Object} filters - Optional filters (destinationId, priceRange, featured)
 * @returns {Promise<Array>} List of travel packages
 */
export const getTravelPackages = async (filters = {}) => {
  try {
    let query = supabase?.from('travel_packages')?.select('*, destinations(name, country, image_url, image_alt)')?.eq('is_active', true)?.order('total_bookings', { ascending: false });

    if (filters?.destinationId) {
      query = query?.eq('destination_id', filters?.destinationId);
    }
    if (filters?.priceRange) {
      query = query?.eq('price_range', filters?.priceRange);
    }
    if (filters?.featured !== undefined) {
      query = query?.eq('is_featured', filters?.featured);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching travel packages:', error);
    throw error;
  }
};

/**
 * Fetch a single travel package by ID
 * @param {string} id - Travel package ID
 * @returns {Promise<Object>} Travel package details
 */
export const getTravelPackageById = async (id) => {
  try {
    const { data, error } = await supabase?.from('travel_packages')?.select('*, destinations(name, country, image_url, image_alt)')?.eq('id', id)?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching travel package:', error);
    throw error;
  }
};

/**
 * Create a new travel package
 * @param {Object} packageData - Travel package information
 * @returns {Promise<Object>} Created travel package
 */
export const createTravelPackage = async (packageData) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    
    const { data, error } = await supabase?.from('travel_packages')?.insert([{
        ...packageData,
        created_by: user?.id
      }])?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating travel package:', error);
    throw error;
  }
};

/**
 * Update a travel package
 * @param {string} id - Travel package ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated travel package
 */
export const updateTravelPackage = async (id, updates) => {
  try {
    const { data, error } = await supabase?.from('travel_packages')?.update({
        ...updates,
        updated_at: new Date()?.toISOString()
      })?.eq('id', id)?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating travel package:', error);
    throw error;
  }
};

/**
 * Delete a travel package
 * @param {string} id - Travel package ID
 * @returns {Promise<boolean>} Success status
 */
export const deleteTravelPackage = async (id) => {
  try {
    const { error } = await supabase?.from('travel_packages')?.delete()?.eq('id', id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting travel package:', error);
    throw error;
  }
};

// ==================== SEARCH & DISCOVERY ====================

/**
 * Search across all travel entities
 * @param {string} searchTerm - Search query
 * @param {Array} entities - Entity types to search (destinations, hotels, activities, restaurants)
 * @returns {Promise<Object>} Search results grouped by entity type
 */
export const searchTravelData = async (searchTerm, entities = ['destinations', 'hotels', 'activities', 'restaurants']) => {
  try {
    const results = {};

    if (entities?.includes('destinations')) {
      const { data } = await supabase?.from('destinations')?.select('*')?.or(`name.ilike.%${searchTerm}%,country.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`)?.eq('is_active', true)?.limit(10);
      results.destinations = data || [];
    }

    if (entities?.includes('hotels')) {
      const { data } = await supabase?.from('hotels')?.select('*, destinations(name, country)')?.or(`name.ilike.%${searchTerm}%,city.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`)?.eq('is_active', true)?.limit(10);
      results.hotels = data || [];
    }

    if (entities?.includes('activities')) {
      const { data } = await supabase?.from('activities')?.select('*, destinations(name, country)')?.or(`name.ilike.%${searchTerm}%,location.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`)?.eq('is_active', true)?.limit(10);
      results.activities = data || [];
    }

    if (entities?.includes('restaurants')) {
      const { data } = await supabase?.from('restaurants')?.select('*, destinations(name, country)')?.or(`name.ilike.%${searchTerm}%,city.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`)?.eq('is_active', true)?.limit(10);
      results.restaurants = data || [];
    }

    return results;
  } catch (error) {
    console.error('Error searching travel data:', error);
    throw error;
  }
};

/**
 * Get featured content across all entities
 * @returns {Promise<Object>} Featured destinations, hotels, activities, packages
 */
export const getFeaturedContent = async () => {
  try {
    const [destinations, hotels, activities, packages] = await Promise.all([
      getDestinations({ featured: true }),
      getHotels({ featured: true }),
      getActivities({ featured: true }),
      getTravelPackages({ featured: true })
    ]);

    return {
      destinations: destinations?.slice(0, 6),
      hotels: hotels?.slice(0, 6),
      activities: activities?.slice(0, 6),
      packages: packages?.slice(0, 6)
    };
  } catch (error) {
    console.error('Error fetching featured content:', error);
    throw error;
  }
};

export default {
  // Destinations
  getDestinations,
  getDestinationById,
  createDestination,
  updateDestination,
  deleteDestination,
  
  // Hotels
  getHotels,
  getHotelById,
  createHotel,
  updateHotel,
  deleteHotel,
  addHotelAmenity,
  
  // Transport
  getTransportOptions,
  getTransportOptionById,
  createTransportOption,
  updateTransportOption,
  deleteTransportOption,
  
  // Activities
  getActivities,
  getActivityById,
  createActivity,
  updateActivity,
  deleteActivity,
  
  // Restaurants
  getRestaurants,
  getRestaurantById,
  createRestaurant,
  updateRestaurant,
  deleteRestaurant,
  
  // Attractions
  getAttractions,
  getAttractionById,
  createAttraction,
  updateAttraction,
  deleteAttraction,
  
  // Travel Packages
  getTravelPackages,
  getTravelPackageById,
  createTravelPackage,
  updateTravelPackage,
  deleteTravelPackage,
  
  // Search & Discovery
  searchTravelData,
  getFeaturedContent
};